import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { timer } from 'rxjs';
import { Colours } from '../../../../../../design-system/colours';
import { ANIMATION_DURATION } from '../../../../../../design-system/design-system.constants';
import { UI_STRINGS } from '../../../../../../functions/language/uistrings-tolgee';
import { Language } from '../../../../../../model/enums/language';
import { TranslateStringPipe } from '../../../../../../pipes/translate-string/translate-string.pipe';
import { LanguageService } from '../../../../../../services/language.service';
import { IconComponent } from '../../../../components/icon/icon.component';
import { GlobalIcon } from '../../../../components/icon/icon.constants';
import { CapitalizeFirstLetterPipe } from '../../../../pipes/capitalize-first-letter.pipe';
import { ButtonComponent } from '../../../buttons/button/button.component';
import { ButtonDirective } from '../../../buttons/button/button.directive';

export type OpenInformationDialogData = {
  type: 'information' | 'success' | 'warning' | 'danger' | 'error';
  title: string;
  text: string;
  details?: string;
  closeLabel?: string;
  acceptLabel?: string;
  rejectLabel?: string;
  ignoreReadOnly?: boolean;
  preserveTitleFormatting?: boolean;
  id?: string;
  role?: string;
};

export type CloseInformationDialogData = 'accepted' | 'rejected' | 'closed' | void;

@Component({
  selector: 'app-information-dialog',
  imports: [
    ButtonComponent,
    ButtonDirective,
    CapitalizeFirstLetterPipe,
    CommonModule,
    IconComponent,
    TranslateStringPipe,
  ],
  templateUrl: './information-dialog.component.html',
  styleUrls: ['./information-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class InformationDialogComponent {
  openDialogData = input.required<OpenInformationDialogData>();
  closeDialogData = output<CloseInformationDialogData>();

  private _languageService = inject(LanguageService);

  isEnglishTitle = computed(() => this._languageService.language === Language.ENGLISH);

  private _confirmButton = viewChild<ButtonComponent>('confirmButton');
  private _confirmButtonElement = viewChild('confirmButton', { read: ElementRef });

  readonly COLOUR = Colours;
  readonly DEFAULT_CLOSE_LABEL = UI_STRINGS.GLOBAL.CLOSE;
  readonly ICON = GlobalIcon;

  constructor() {
    effect(() => {
      const confirmButtonComponentAutofocus = this._confirmButton()?.autofocus;
      const confirmButtonElementButton = this._confirmButtonElement()?.nativeElement?.firstChild;

      if (confirmButtonComponentAutofocus && confirmButtonElementButton) {
        timer(ANIMATION_DURATION).subscribe(() => {
          confirmButtonElementButton?.focus?.();
        });
      }
    });
  }
}
